import { Injectable } from '@angular/core';
import { OrganizationService } from './organization.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';
import { Organization } from '../models/model';
import { ApiService } from './api.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import moment from 'moment';
import { OrganizationUtilService } from './organization-util.service';
import _ from 'lodash';
import { SessionOptionsService } from './session-option.service';
import { Injector } from '@angular/core';
import { DeviceService } from './device.service';
import { WindowService } from './window.service';
import { ThreeIntegrationService } from './three-js-building.service';

@Injectable({
  providedIn: 'root'
})
export class IllumLiveService {
  orgnizationId = '0';
  private _dictionaryAffectedPerimeters: Record<string, any[]> = {};
  public papiPerimeters: Record<string, any> = {}; // Populate this as needed
  public foreverEntitiesAdded: { perimeters: Record<string, any> } = { perimeters: {} };
  private lastClosestTimestamp: number | undefined;
  private isPullingPlaybackBuildingViewData = false;
  private _sessionOptionsService?: SessionOptionsService;
  private _managingOrgId: string | undefined;
  constructor(
    private api: ApiService,
    private orgService: OrganizationService,
    private orgnizationUtilService: OrganizationUtilService,
    private injector: Injector,
    private deviceService: DeviceService,
    private windowService: WindowService,
    private threeJsBuildingIntegration: ThreeIntegrationService
  ) { }

  private get sessionOptionsService(): SessionOptionsService {
    if (!this._sessionOptionsService) {
      this._sessionOptionsService = this.injector.get(SessionOptionsService);
    }
    return this._sessionOptionsService;
  }

  getUserPermissionsAndOrgIds(): Observable<{ myOrgs: Organization[] }> {
    return this.orgService.getOrganizations().pipe(
      map((orgs: any) => {
        const tincanorgs = orgs.rows.map((org: any) => {
          const shellOrg = new Organization();
          shellOrg.id = org.id;
          shellOrg.name = org.name;
          return shellOrg;
        });
        return { myOrgs: tincanorgs };
      })
    );
  }
  getAllIncidentsForPlaybackTree(): Observable<any[]> {
    const params = {
      attributes: [
        "id", "name", "status", "description", "createdAt", "updatedAt"
      ],
      limit: 10000,
      where: { type: ["PERIMETER", "GEOFENCE"] }
    };
    const params2 = {
      attributes: [
        "incidentId", "organizationId", "createdAt", "updatedAt"
      ],
      limit: 10000
    };
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.IncidentCountAllFromOrganization;
    const url2 =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.IncidentOrganizationFind

    return forkJoin({
      incidents: this.api.post<any>(url, params),
      incidentOrgs: this.api.post<any>(url2, params2)
    }).pipe(
      map(response => {
        const orgIds: string[] = Array.from(
          new Set(
            response.incidentOrgs.rows.map((row: any) => row.organizationId)
          )
        ) as string[];
        const incidentDataByOrg = orgIds.map((orgId) => {
          const orgIncidentIds = response.incidentOrgs.rows.filter(
            (row: any) => row.organizationId === orgId
          );
          return response.incidents.rows
            .filter((incident: any) =>
              orgIncidentIds.some((o: any) => o.incidentId === incident.id)
            )
            .map((incident: any) => ({
              ...incident,
              organization: orgId,
              label: incident.name,
              status: incident.status.toLowerCase()
            }));
        });
        return incidentDataByOrg;
      })
    );
  }

  private trackingIncidentTransform(
    incident: any,
    incidentOrgs: any[],
    device: any,
    user: any
  ): any {
    const originatorOrgId =
      incidentOrgs && incidentOrgs.length > 0
        ? incidentOrgs[0].organizationId
        : undefined;

    return {
      id: incident.id,
      status: incident.status,
      profileId: user ? user.id : null,
      deviceId: device ? device.id : null,
      managingOrganization: originatorOrgId,
      title: incident.name,
      description: incident.description,
      trackingPlatform: device ? device.type : null,
      begin: incident.createdAt,
      createdAt: incident.createdAt,
      end: incident.endAt,
      emergencyContacts: user ? user.UserEmergencyContacts : null,
      checkInTime: undefined,
      panicTimes: [],
      resolvedTimes: [],
    };
  }

  getAllActiveTrackingEventsForPlaybackTree(): Observable<any> {
    const params = {
      attributes: [
        "status",
        "name",
        "description",
        "createdAt",
        "updatedAt",
        "endAt",
      ],
      where: { type: "TRACKING" },
      limit: 10000
    };
    const params2 = {
      attributes: ["incidentId", "organizationId"],
      limit: 10000
    };

    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentCountAllFromOrganization;
    const url2 = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentOrganizationFind;

    return forkJoin({
      incidents: this.api.post<any>(url, params),
      incidentOrgs: this.api.post<any>(url2, params2)
    }).pipe(
      map(response => {
        if (!response || !response.incidents.rows || response.incidents.rows.length === 0) {
          return {};
        }

        const tincanTrackings = response.incidents.rows.map((incident: any) => {
          const device = null;
          const user = null;

          const incidentOrgs = response.incidentOrgs.rows.filter((org: any) =>
            org.incidentId === incident.id
          );

          return this.trackingIncidentTransform(
            incident,
            incidentOrgs,
            device,
            user
          );
        });

        const data = tincanTrackings.reduce((acc: any, value: any) => {
          acc[value.id] = this.createNewActiveTrackingEvent(value);
          return acc;
        }, {});

        return data;
      }),
      catchError((error: any) => {
        console.error("Error getting tracking incidents.", error);
        return of({});
      })
    );
  }

  getAllProcallsForPlaybackTree(): Observable<any[]> {
    const params = {
      where: { type: "SUPPORT" },
      limit: 10000
    };
    const params2 = {
      attributes: ["incidentId", "organizationId"],
      limit: 10000
    };

    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentCountAllFromOrganization;
    const url2 = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentOrganizationFind;

    return forkJoin({
      incidents: this.api.post<any>(url, params),
      incidentOrgs: this.api.post<any>(url2, params2)
    }).pipe(
      map(response => {
        const procalls = response.incidents.rows.map((procall: any) => {
          const incOrgs = response.incidentOrgs.rows.filter((org: any) =>
            org.incidentId === procall.id
          );
          procall.organizationId = incOrgs.length > 0 ? incOrgs[0].organizationId : undefined;
          procall.status =
            procall.status === "ACTIVE" ? "open" : procall.status.toLowerCase();
          procall.title = procall.name;
          return procall;
        });
        return procalls;
      }),
      catchError((error: any) => {
        console.error("Error getting procalls.", error);
        return of([]);
      })
    );
  }

  private createNewActiveTrackingEvent(value: any): any {
    value.getFormattedDate = function () {
      return (moment as any)(this.begin).format("MMM DD hh:mm a");
    };
    value.getFormattedEndDate = function () {
      return (moment as any)(this.end).format("MMM DD hh:mm a");
    };
    value.timestamp = (moment as any)(value.begin)
      .toDate()
      .getTime();

    return value;
  }

  getIncident(eventId: string): Observable<{ data: { items: any[] } }> {
    return this.getAllIncidents(null, eventId, this.orgnizationUtilService.data).pipe(
      map(response => ({ data: { items: [response[0]] } }))
    );
  }
  getData() {
    return this.orgnizationUtilService.data;
  };
  // API for monitor mode service
getAllIncidents(statuses?: any, incidentId?: string, serviceData?: any): Observable<any[]> {
  // Build params for the main incident query
  let params: { order: string[][]; where: Record<string, any>; limit: number } = {
    order: [["createdAt", "ASC"]],
    where: {},
    limit: 10000,
  };

  if (incidentId) {
    params.where['id'] = incidentId;
  }

  if (statuses) {
    params.where['status'] = statuses.toUpperCase().split(",");
  }

  params.where['type'] = ["PERIMETER", "GEOFENCE"];
  const baseUrl = ConfigurationManagerService.settings.BaseURL;

  return this.api.post<any>(
    baseUrl + ConfigurationManagerService.settings.IncidentCountAllFromOrganization,
    params
  ).pipe(
    switchMap((response: any) => {
      const incidents = response.rows;
      if (!incidents.length) return of([]);

      const incidentIds = incidents.map((i: any) => i.id);
      const idWhere = { incidentId: incidentIds };

      return forkJoin({
        incidents: of(incidents),
        incidentPerimeters: this.api.post<any>(
          baseUrl + 'db/IncidentPerimeter/findAndCountAll',
          {
            where: idWhere,
            attributes: ["perimeterId", "incidentId"],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
        geofence: this.api.post<any>(
          baseUrl + ConfigurationManagerService.settings.GeofenceFindAndCountAllFromOrganization,
          {
            where: idWhere,
            attributes: ["incidentId", "rating", "geometry", "updatedAt"],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
        safezones: this.api.post<any>(
          baseUrl + ConfigurationManagerService.settings.findAndCountAllFromOrganization,
          {
            where: idWhere,
            attributes: [
              "id", "incidentId", "active", "geometry", "label", "description", "updatedAt", "perimeterSafezoneId"
            ],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
        orgIncidents: this.api.post<any>(
          baseUrl + ConfigurationManagerService.settings.IncidentOrganizationFind,
          {
            where: idWhere,
            attributes: ["incidentId", "organizationId"],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
        updates: this.api.post<any>(
          baseUrl + 'db/IncidentUpdate/findAndCountAllFromOrganization',
          {
            where: { ...idWhere, status: { $ne: "REJECTED" } },
            attributes: [
              "incidentId", "visibility", "description", "incidentUpdateAttachmentId",
              "position", "status", "createdAt", "updatedAt", "incidentDeviceId", "perimeterId"
            ],
            order: [["updatedAt", "DESC"]],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
        focalPoints: this.api.post<any>(
          baseUrl + 'db/Focal/findAndCountAllFromOrganization',
          {
            where: idWhere,
            attributes: ["incidentId", "position"],
            limit: 10000
          }
        ).pipe(catchError(() => of({ rows: [] }))),
      }).pipe(
        switchMap((response) => {
          const tincanIncidents = this.transformIncidents(
            response.incidents,
            response.geofence.rows,
            response.orgIncidents.rows,
            response.safezones.rows,
            response.updates.rows,
            response.focalPoints.rows,
            response.incidentPerimeters.rows
          );

          const allAffectedPerimeters = this.orgnizationUtilService.data
            ? this.collectIncidentAffectedPerimetersAndBeacons(
              this.orgnizationUtilService.data,
              response.incidentPerimeters.rows
            ) : {};

          return forkJoin({
            tincanIncidents: of(tincanIncidents),
            allAffectedPerimeters: of(allAffectedPerimeters),
            allAttachments: this.collectUpdateAttachments(tincanIncidents),
            incidentUpdateUserIds: this.getIncidentUpdateUserIds(response.updates.rows)
          });
        }),
        map((finalResponse) => {
          const processedTincanIncidents = finalResponse.tincanIncidents.map((incident) => {
            incident.affectedPerimeters = finalResponse.allAffectedPerimeters[incident.id];
            if (incident.updates) {
              incident.updates.forEach((update: any) => {
                update.userInfo = finalResponse.incidentUpdateUserIds[update.incidentDeviceId];
              });
            }
            return incident;
          });

          if (finalResponse.allAttachments && (finalResponse.allAttachments as any).rows) {
            this.buildUpdateAttachmentResults(
              processedTincanIncidents,
              (finalResponse.allAttachments as any).rows
            );
          }

          return processedTincanIncidents;
        })
      );
    }),
    catchError((err) => {
      console.error('Error getting all incidents', err);
      return of([]);
    })
  );
}


  private collectIncidentAffectedPerimetersAndBeacons(
    thisService: { papiPerimeters: Record<string, any> },
    incidentPerimeters: any[]
  ): Record<string, any[]> {
    if (!incidentPerimeters || incidentPerimeters.length === 0) {
      return {};
    }

    const perimeterIds = incidentPerimeters.map((ip: any) => ip.perimeterId);

    // Check cache
    if (
      perimeterIds.filter(
        (id) => !(id in this._dictionaryAffectedPerimeters)
      ).length === 0
    ) {
      return this._dictionaryAffectedPerimeters;
    }

    const perimeters = perimeterIds
      .map((perimeterId) => thisService.papiPerimeters[perimeterId])
      .filter(Boolean);

    // Collect perimeter descendants
    const allPerimeterIds = Array.from(
      new Set(
        perimeters.flatMap((perimeter) =>
          [perimeter.id, ...(perimeter.descendants || [])]
        )
      )
    );

    const allPerimeters = allPerimeterIds
      .map((perimeterId) => thisService.papiPerimeters[perimeterId])
      .filter(Boolean);

    // Collect all for all incidents
    const allAffectedPerimeters = allPerimeters.map((perimeter) => {
      const affectedPerm: any = {
        perimeterId: perimeter.id,
      };
      if (
        !perimeter.beacons ||
        (Array.isArray(perimeter.beacons) && perimeter.beacons.length === 0) ||
        (typeof perimeter.beacons === 'object' && Object.keys(perimeter.beacons).length === 0)
      ) {
        affectedPerm.noBeacons = true;
      } else {
        affectedPerm.beacons = perimeter.beacons;
      }
      return affectedPerm;
    });

    // Collect affectedPerimeter objects per incident, including descendants
    const incidentIds = Array.from(
      new Set(incidentPerimeters.map((ip: any) => ip.incidentId))
    );
    incidentIds.forEach((incidentId) => {
      // Perimeters for this incident
      const incPerimeterIds = incidentPerimeters
        .filter((ip: any) => ip.incidentId === incidentId)
        .map((ip: any) => ip.perimeterId);

      const incAffectedPerimeters: any[] = [];
      allAffectedPerimeters.forEach((affectedPerimeter) => {
        const perimeter = allPerimeters.find(
          (p) => p.id === affectedPerimeter.perimeterId
        );
        if (perimeter && incPerimeterIds.includes(perimeter.id)) {
          // Directly tagged
          incAffectedPerimeters.push(affectedPerimeter);
          // Now collect descendants
          allAffectedPerimeters.forEach((checkDescendant) => {
            if (
              perimeter.descendants &&
              perimeter.descendants.includes(checkDescendant.perimeterId)
            ) {
              incAffectedPerimeters.push(checkDescendant);
            }
          });
        }
      });
      // Save subset for this incidentId
      this._dictionaryAffectedPerimeters[incidentId] = incAffectedPerimeters;
    });

    return this._dictionaryAffectedPerimeters;
  }

  private collectUpdateAttachments(tincanIncidents: any[]): any {
    const attachmentIds: string[] = [];

    tincanIncidents.forEach((incident: any) => {
      if (incident.updates) {
        incident.updates.forEach((update: any) => {
          if (update.attachment !== undefined && update.attachment !== null) {
            attachmentIds.push(update.attachment);
          }
        });
      }
    });

    if (attachmentIds.length === 0) {
      return of({ count: 0, rows: [] });
    }

    const baseUrl = ConfigurationManagerService.settings.BaseURL;
    const params = {
      where: { 
        deletedAt: 'Infinity',
        id: attachmentIds 
      },
      attributes: ['id', 'name'],
      limit: 10000
    };

    return this.api.post<any>(
      baseUrl + 'db/IncidentUpdateAttachment/findAndCountAllFromOrganization',
      params
    ).pipe(
      map((result: any) => result),
      catchError((error) => {
        console.error('Error retrieving IncidentUpdateAttachment', error);
        return of({ count: 0, rows: [] });
      })
    );
  }
  getIncidentUpdateUserIds(updates: any): Observable<Record<string, any>> {
    // Step 1: Collect incidentDeviceIds from updates
    const incidentDeviceIds = (updates || [])
      .map((u: { incidentDeviceId: any; }) => u.incidentDeviceId)
      .filter(Boolean);

    if (!incidentDeviceIds.length) {
      return of({});
    }

    // Step 2: Fetch incident devices
    const incidentDeviceParams = {
      attributes: ['deviceId', 'id'],
      where: { id: incidentDeviceIds },
      limit: 10000
    };

    return this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + 'db/IncidentDevice/findAndCountAllFromOrganization',
      incidentDeviceParams
    ).pipe(
      switchMap((incidentDeviceResponse: any) => {
        const incidentDeviceInfo = incidentDeviceResponse?.rows || [];
        const deviceIds = incidentDeviceInfo.map((d: any) => d.deviceId).filter(Boolean);

        if (!deviceIds.length) {
          return of({});
        }

        // Step 3: Fetch devices
        const deviceParams = {
          attributes: ['userId', 'id'],
          where: { id: deviceIds },
          limit: 10000
        };

        return this.api.post<any>(
          ConfigurationManagerService.settings.BaseURL + 'db/Device/findAndCountAllFromOrganization',
          deviceParams
        ).pipe(
          map((deviceResponse: any) => {
            const deviceInfo = deviceResponse?.rows || [];
            // Attach userId to incidentDeviceInfo
            incidentDeviceInfo.forEach((incidentDevice: any) => {
              const device = deviceInfo.find((d: any) => d.id === incidentDevice.deviceId);
              if (device) {
                incidentDevice.userId = device.userId;
              }
            });
            return incidentDeviceInfo;
          })
        );
      }),
      switchMap((incidentDeviceInfo: any[]) => {
        const userIds = Array.from(new Set(incidentDeviceInfo.map((d: any) => d.userId).filter(Boolean)));
        if (!userIds.length) {
          return of({});
        }

        // Step 4: Fetch users
        const userParams = {
          attributes: ['name', 'id'],
          where: { id: userIds },
          limit: 10000
        };

        return this.api.post<any>(
          ConfigurationManagerService.settings.BaseURL + 'db/User/findAndCountAllUsers',
          userParams
        ).pipe(
          map((userResponse: any) => {
            const userInfo = userResponse?.rows || [];
            // Attach name to incidentDeviceInfo
            incidentDeviceInfo.forEach((incidentDevice: any) => {
              const user = userInfo.find((u: any) => u.id === incidentDevice.userId);
              if (user) {
                incidentDevice.name = user.name;
              }
            });

            // Build info object
            const info: Record<string, any> = {};
            incidentDeviceInfo.forEach((device: any) => {
              info[device.id] = device;
            });
            return info;
          })
        );
      }),
      catchError((err) => {
        console.error('Error in getIncidentUpdateUserIds', err);
        return of({});
      })
    );
  }

  private buildUpdateAttachmentResults(tincanIncidents: any[], attachments: any[]): void {
    // Transform attachments array into a dictionary keyed by id
    const attachmentMap: Record<string, any> = {};
    attachments.forEach((attachment: any) => {
      attachmentMap[attachment.id] = attachment;
    });

    if (attachments.length > 0) {
      tincanIncidents.forEach((incident: any) => {
        if (incident.updates) {
          incident.updates.forEach((update: any) => {
            if (update.attachment != null && update.attachment != undefined) {
              const attachmentData = attachmentMap[update.attachment];
              if (attachmentData) {
                update.attachment = `${attachmentData.id}.${attachmentData.name}`;
              }
            }
          });
        }
      });
    }
  }
  private collectUpdateAttachment(incidents: any[], history = false): Observable<any> {
    // Collect all attachment IDs from incident updates
    const ids: string[] = [];
    incidents.forEach((incident: any) => {
      if (incident.updates) {
        incident.updates.forEach((update: any) => {
          if (update.attachment !== undefined && update.attachment !== null) {
            ids.push(update.attachment);
          }
        });
      }
    });

    // If no IDs, return Observable of empty result
    if (!ids.length) {
      return of({ count: 0, rows: [] });
    }

    // Prepare API params
    let param: any = {
      attributes: ['id', 'name'],
      where: {
        deletedAt: 'Infinity',
      },
      limit: 10000,
    };

    if (!history) {
      param.where.id = ids;
    } else {
      param.where.incidentUpdateAttachmentId = ids;
    }

    // Choose endpoint based on history flag
    const endpoint = !history
      ? 'db/IncidentUpdateAttachment/findAndCountAll'
      : 'db/IncidentUpdateAttachmentHistory/findAndCountAll';

    // Make API call
    return this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + endpoint,
      param
    ).pipe(
      map((result: any) => {
        if (history && result && result.rows) {
          result.rows = result.rows.map((row: any) => ({
            id: row.id,
            name: row.name,
          }));
        }
        return result;
      }),
      catchError((error) => {
        console.error('Error retrieving IncidentUpdateAttachment', error);
        return of({ count: 0, rows: [] });
      })
    );
  }
  private findFences(geofences: any[], incidentId: string, rating: string): any[] {
    return geofences.filter(
      fence =>
        fence.incidentId === incidentId && fence.rating === rating
    );
  }
  setManagingOrgId(id: string): any {
    this.orgnizationId = id;
    this._managingOrgId = id;
  }
  getManagingOrgId() {
    return this.orgnizationId;
  }

  private findLatestUpdate(dates: (string | undefined)[]): string {
    let max = "1900-01-01T00:00:00.000";
    dates.forEach(date => {
      if (date && Date.parse(date) > Date.parse(max)) {
        max = date;
      }
    });
    return max;
  }

  private incidentUpdates(incidentId: string, updates: any[], myPerimeters?: Record<string, any>): any[] {
    updates = updates || [];
    return updates
      .filter(update => update.incidentId === incidentId)
      .map(update => {
        let altitude = 0;
        if (myPerimeters && update.perimeterId && myPerimeters[update.perimeterId]) {
          altitude = myPerimeters[update.perimeterId].height;
        }
        const tincanUpdate: any = {
          id: update.id,
          isPublic: update.visibility === "PUBLIC",
          description: update.description,
          attachment: update.incidentUpdateAttachmentId,
          geometry: update.position,
          updatedAt: update.updatedAt,
          lastUpdate: update.updatedAt,
          createdAt: update.createdAt,
          validAt: update.validAt,
          incidentDeviceId: update.incidentDeviceId,
          status: update.status,
          perimeterId: update.perimeterId,
          altitude: altitude,
        };
        if (update.status === "APPROVED") {
          tincanUpdate.approvedBy = "Unknown";
        }
        if (update.status === "REJECTED") {
          tincanUpdate.rejectedBy = "Unknown";
        }
        return tincanUpdate;
      });
  }

  private transformIncidents(
    incidents: any[],
    geofences: any[],
    orgIncidents: any[],
    safezones: any[],
    updates: any[],
    focalPoints: any[],
    incidentPerimeters: any[],
    myPerimeters: Record<string, any> = {},
    papiPerimeters: Record<string, any> = {}
  ): any[] {
    if (!incidents || !geofences || !orgIncidents) {
      return [];
    }

    return incidents.map((incident) => {
      // Use lodash deep clone to avoid mutating the original
      const tincanIncident = _.cloneDeep(incident);

      const highFences = this.findFences(geofences, incident.id, "HIGH");
      const medFences  = this.findFences(geofences, incident.id, "MEDIUM");
      const lowFences  = this.findFences(geofences, incident.id, "LOW");

      // Handle PERIMETER fallback if no HIGH fence exists
      if (highFences.length === 0 && incident.type === "PERIMETER") {
        const incPerimeter = incidentPerimeters.find((p: any) => p.incidentId === incident.id);
        if (incPerimeter) {
          const perimeter =this.orgnizationUtilService.data.papiPerimeters[ incPerimeter.perimeterId];
          if (perimeter) {
            highFences.push({geometry: perimeter.geometry,
              updatedAt: perimeter.updatedAt
            });
            tincanIncident.incidentPerimeter =  this.orgnizationUtilService.data.myPerimeters[perimeter.id];
          }
        }
      }

      tincanIncident.eventId = incident.id;
      tincanIncident.highGeometries = highFences.map(f => f.geometry);
      tincanIncident.mediumGeometries = medFences.map(f => f.geometry);
      tincanIncident.lowGeometries = lowFences.map(f => f.geometry);
      tincanIncident.status = incident.status.toLowerCase();
      tincanIncident.label = incident.name;
      tincanIncident.description = incident.description;
      tincanIncident.updates = this.incidentUpdates(incident.id, updates,  this.orgnizationUtilService.data.myPerimeters);
      tincanIncident.firstResponderOrgs = [];
      tincanIncident.safezones = (safezones || [])
        .filter(sz => sz.incidentId === incident.id)
        .map(sz => ({
          ...sz,
          isActive: sz.active,
          perimeterSafezoneId: sz.perimeterSafezoneId
        }));

      const allFenceDates = [
        ...highFences.map(f => f.updatedAt),
        ...medFences.map(f => f.updatedAt),
        ...lowFences.map(f => f.updatedAt),
        incident.updatedAt
      ].filter(Boolean);
      tincanIncident.updatedAt = this.findLatestUpdate(allFenceDates);
      tincanIncident.isPublic = incident.type === "PERIMETER";
      tincanIncident.type = incident.type;

      if (focalPoints) {
        const fps = focalPoints.filter(fp => fp.incidentId === incident.id);
        tincanIncident.focalPoint = fps.length > 0 ? fps[0].position : undefined;
      }

      // Find org that created the incident
      const orgsForIncident = orgIncidents.filter(oi => oi.incidentId === incident.id);
      tincanIncident.organization = orgsForIncident.length > 0 ? orgsForIncident[0].organizationId : undefined;
      if (orgsForIncident.length > 1) {
        tincanIncident.organizationIds = orgsForIncident.map((org: any) => org.organizationId);
      }
      return tincanIncident;
    });
  }
  getAllProcalls(getResolved?: boolean, procallId = null): Observable<any[]> {
    const params: any = { body: { where: { type: "SUPPORT" } } };

    if (getResolved === true) {
      params.body.where.status = "RESOLVED";
    } else if (getResolved === false) {
      params.body.where.status = { $ne: "RESOLVED" };
    }
    if (procallId) {
      params.body.where.id = procallId;
    }
    return this.getIncidentDeviceUserData(params);
  }

  getIncidentDeviceUserData(incidentParams: any): Observable<any> {
    // Step 1: Set attributes if needed
    if (incidentParams?.body) {
      incidentParams.body.attributes = [
        "name", "description", "status", "createdAt", "updatedAt", "endAt"
      ];
    }

    // Step 2: Fetch incidents
    return this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentCountAllFromOrganization,
      incidentParams.body
    ).pipe(
      switchMap((response: any) => {
        const incidents = response?.rows || [];
        if (!incidents.length) return of(null);

        const incidentIds = incidents.map((i: any) => i.id);

        // Step 3: Fetch orgs and devices in parallel
        const orgParams = {
          where: { incidentId: incidentIds },
          attributes: ["incidentId", "organizationId"],
          order: [["createdAt", "ASC"]],
          limit: 10000
        };
        const deviceParams = {
          where: { incidentId: incidentIds },
          attributes: ["deviceId", "incidentId"],
          order: [["createdAt", "ASC"]],
          limit: 10000
        };

        return forkJoin({
          incidents: of(incidents),
          incidentOrgs: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentOrganizationFind,
            orgParams
          ),
          incidentDevices: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentDeviceFindAndCountAllFromOrganization,
            deviceParams
          )
        });
      }),
      switchMap((response: any) => {
        if (!response) return of(null);

        const deviceIds = [...new Set((response.incidentDevices?.rows || []).map((d: any) => d.deviceId))];
        if (!deviceIds.length) return of({ ...response, devices: [], users: [] });

        // Step 4: Fetch devices
        const deviceFetchParams = {
          where: { id: deviceIds },
          attributes: ["userId", "type"]
        };

        return forkJoin({
          ...response,
          devices: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.DeviceFindAndCountAllFromOrganization,
            deviceFetchParams
          )
        });
      }),
      switchMap((response: any) => {
        if (!response) return of(null);

        const userIds = (response.devices?.rows || []).map((d: any) => d.userId);
        if (!userIds.length) return of({ ...response, users: [] });

        // Step 5: Fetch users
        const userFetchParams = { where: { id: userIds } };

        return forkJoin({
          ...response,
          users: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.UserProfilesFromOrganization,
            userFetchParams
          )
        });
      }),
      map((response: any) => {
        if (!response) return null;
        return {
          incidents: response.incidents,
          incidentOrgs: response.incidentOrgs?.rows || [],
          incidentDevices: response.incidentDevices?.rows || [],
          devices: response.devices?.rows || [],
          users: response.users?.rows || []
        };
      }),
      catchError((err) => {
        console.error('Error in getIncidentDeviceUserData', err);
        return of(null);
      })
    );
  }

  getCurrentActiveTrackingEvents(): Observable<any> {
    return this.getTrackingEventsInternal(null, true);
  }
  getTrackingEventsInternal(eventId: any, getActiveOnly: boolean): Observable<any> {
    const params: any = { body: { where: { type: "TRACKING" } } };
    if (eventId) {
      params.body.where.id = eventId;
    }
    if (getActiveOnly) {
      params.body.where.status = { $ne: "RESOLVED" };
    }

    return this.getIncidentDeviceUserData(params).pipe(
      map((response: any) => {
        if (!response) return {};

        // Map each incident to a tracking event object
        const tincanTrackings = (response.incidents || []).map((incident: any) => {
          const incidentDevice = (response.incidentDevices || []).find(
            (idv: any) => idv.incidentId === incident.id
          ) || { deviceId: null };

          const device = (response.devices || []).find(
            (d: any) => d.id === incidentDevice.deviceId
          ) || { userId: null };

          const user = (response.users || []).find(
            (u: any) => u.id === device.userId
          );

          const incidentOrgs = (response.incidentOrgs || []).filter(
            (org: any) => org.incidentId === incident.id
          );

          // You must implement or inject transforms.trackingIncident and createNewActiveTrackingEvent
          return this.transforms.trackingIncident(
            incident,
            incidentOrgs,
            device,
            user
          );
        });

        // Assemble the dictionary of trackingIncidents
        const data: Record<string, any> = {};
        tincanTrackings.forEach((value: any) => {
          const event = this.createNewActiveTrackingEvent(value);
          data[event.id] = event;
        });

        return data;
      }),
      catchError((err) => {
        console.error("Error getting tracking incidents.", err);
        return of({});
      })
    );
  }
  getPlaybackIncident(eventId: string): Observable<any> {
    // 1. First, get the incident rows
    const params = { where: { incidentId: eventId } };
    return this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/IncidentHistory/findAndCountAll', params).pipe(
      switchMap((response: any) => {
        if (!response || !response.rows.length) {
          return of({ items: [] });
        }

        // 2. Prepare all the other calls
        const body1 = {
          order: [['validAt', 'ASC']],
          where: { incidentId: eventId }
        };
        const body2 = {
          order: [['createdAt', 'ASC']],
          where: { incidentId: eventId }
        };

        const incidentPerimeterParams = _.merge({ attributes: ['perimeterId', 'incidentId'] }, body2);
        const incidentOrganizationParams = _.merge({ attributes: ['incidentId', 'organizationId'] }, body2);
        const geofenceParams = _.merge({ attributes: ['geofenceId', 'incidentId', 'rating', 'geometry', 'updatedAt', 'validAt'] }, body1);
        const safezoneParams = _.merge({ attributes: ['safezoneId', 'id', 'incidentId', 'active', 'geometry', 'label', 'description', 'validAt', 'perimeterSafezoneId'] }, body1);
        const incidentUpdateParams = _.merge({ attributes: ['incidentUpdateId', 'incidentId', 'visibility', 'description', 'incidentUpdateAttachmentId', 'position', 'status', 'createdAt', 'updatedAt', 'validAt', 'perimeterId'] }, body1);
        const focalParams = _.merge({ attributes: ['incidentId', 'position'] }, body2);

        return forkJoin({
          incidents: of(response.rows),
          incidentPerimeters: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/IncidentPerimeter/findAndCountAll', incidentPerimeterParams),
          orgIncidents: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/IncidentOrganization/findAndCountAll', incidentOrganizationParams),
          geofences: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/GeofenceHistory/findAndCountAll', geofenceParams),
          safezones: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/SafezoneHistory/findAndCountAll', safezoneParams),
          updates: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/IncidentUpdateHistory/findAndCountAll', incidentUpdateParams),
          focalPoints: this.api.post<any>(ConfigurationManagerService.settings.BaseURL + 'db/Focal/findAndCountAllFromOrganization', focalParams)
        });
      }),
      switchMap((response: any) => {
        if (!response || (Array.isArray(response) && response.length === 0)) {
          return of({ items: [] });
        }

        // 3. Transform and collect additional data
        const tincanIncidents = this.incidentHistory(
          response.incidents,
          response.geofences.rows,
          response.orgIncidents.rows,
          response.safezones.rows,
          response.updates.rows,
          response.focalPoints.rows,
          response.incidentPerimeters.rows
        );

        return forkJoin({
          tincanIncidents: of(tincanIncidents),
          allAffectedPerimeters: this.collectIncidentAffectedPerimetersAndBeaconsPlayback(response.incidentPerimeters.rows),
          allAttachments: this.collectUpdateAttachment(tincanIncidents)
        });
      }),
      map((response: any) => {
        if (!response) {
          return { items: [] };
        }

        // Attach affectedPerimeters to each incident event
        let tincanIncidents = response.tincanIncidents.map((incident: any) => {
          incident.affectedPerimeters = response.allAffectedPerimeters[incident.id];
          return incident;
        });

        if (response.allAttachments) {
          this.buildUpdateAttachmentResults(tincanIncidents, response.allAttachments.rows);
        }

        return { items: tincanIncidents };
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  incidentHistory(
    incidentHistory: any[],
    geofenceHistory: any[],
    orgIncidents: any[],
    safezoneHistory: any[],
    updateHistory: any[],
    focalPoints: any[],
    incidentPerimeters: any[]
  ): any[] {
    if (!incidentHistory || !geofenceHistory || !orgIncidents) {
      return [];
    }

    // Replace id fields
    incidentHistory = this.replaceHistoryId(incidentHistory, 'incidentId');
    geofenceHistory = this.replaceHistoryId(geofenceHistory, 'geofenceId');
    safezoneHistory = this.replaceHistoryId(safezoneHistory, 'safezoneId');
    updateHistory = this.replaceHistoryId(updateHistory, 'incidentUpdateId');

    // Gather all event times into a single sorted deduped list
    const allEventTimes = _.uniq(
      _.concat(
        _.map(incidentHistory, 'validAt[0]'),
        _.map(geofenceHistory, 'validAt[0]'),
        _.map(safezoneHistory, 'validAt[0]'),
        _.map(updateHistory, 'validAt[0]')
      )
    ).sort();

    const tincanIncidents: any[] = [];
    for (let i = 0; i < allEventTimes.length; i++) {
      const start = allEventTimes[i];
      const end = allEventTimes[i + 1];

      const incident = this.filterByTimeslice(incidentHistory, start, end)[0] || {};
      const highFence = _.filter(this.filterByTimeslice(geofenceHistory, start, end), { rating: 'HIGH' });
      const medFence = _.filter(this.filterByTimeslice(geofenceHistory, start, end), { rating: 'MEDIUM' });
      const lowFence = _.filter(this.filterByTimeslice(geofenceHistory, start, end), { rating: 'LOW' });

      // Safezones and updates are everything from the first event to the end of the current slice
      const safezones = this.filterByTimeslice(safezoneHistory, allEventTimes[0], end);
      const updates = this.filterByTimeslice(updateHistory, allEventTimes[0], end);

      // Add updatedAt field
      incident.updatedAt = start;

      const geofences = _.concat(highFence, medFence, lowFence);

      // Call the standard transform for a single item (for this timeslice)
      // You must implement or import this function!
      const tincanIncident = this.transformIncidents(
        [incident],
        geofences,
        orgIncidents,
        safezones,
        updates,
        focalPoints,
        incidentPerimeters
      );

      tincanIncidents.push(tincanIncident[0]);
    }
    return tincanIncidents;
  }


  collectIncidentAffectedPerimetersAndBeaconsPlayback(incidentPerimeters: any[]): Observable<Record<string, any[]>> {
    if (!incidentPerimeters || incidentPerimeters.length === 0) {
      return of({});
    }

    const incidentPerimeterIds = incidentPerimeters.map(ip => ip.perimeterId);
    if (
      _.difference(incidentPerimeterIds, Object.keys(this._dictionaryAffectedPerimeters)).length === 0
    ) {
      return of(this._dictionaryAffectedPerimeters);
    }

    const perimeterParams = {
      attributes: ["id", "ancestors", "parentId"],
      where: {
        $or: [
          {
            id: incidentPerimeterIds,
          },
          {
            ancestors: {
              $overlap: incidentPerimeterIds,
            },
          },
        ],
      },
      limit: 10000
    };

    const perimeterUrl = ConfigurationManagerService.settings.BaseURL + 'db/Perimeter/findAndCountAll';

    return this.api.post<any>(perimeterUrl, perimeterParams).pipe(
      switchMap((perimeterResponse: any) => {
        if (!perimeterResponse || !perimeterResponse.rows || perimeterResponse.rows.length === 0) {
          return of({});
        }

        const allPerimeters = perimeterResponse.rows.map((perimeter: any) => {
          perimeter.parentPerimeterId = perimeter.parentId;
          return perimeter;
        });

        const perimeterIds = allPerimeters.map((p: any) => p.id);

        const beaconParams = {
          attributes: ["id", "position", "perimeterId"],
          where: {
            perimeterId: perimeterIds,
          },
          limit: 10000
        };

        const beaconUrl = ConfigurationManagerService.settings.BaseURL + 'db/Beacon/findAndCountAll';

        return forkJoin({
          beacons: this.api.post<any>(beaconUrl, beaconParams),
          allPerimeters: of(allPerimeters)
        });
      }),
      map((response: any) => {
        if (!response || !response.allPerimeters) {
          return {};
        }

        const allAffectedPerimeters = response.allPerimeters.map((perimeter: any) => {
          const beacons = this.collectPerimeterBeacons(
            perimeter,
            response.beacons?.rows || []
          );

          const affectedPerm: any = {
            perimeterId: perimeter.id,
            ancestors: perimeter.ancestors,
          };

          if (beacons.length === 0) {
            affectedPerm.noBeacons = true;
          } else {
            affectedPerm.beacons = beacons;
          }

          return affectedPerm;
        });

        const incidentIds = _.uniq(incidentPerimeters.map(ip => ip.incidentId));

        incidentIds.forEach((incidentId) => {
          const incPerimeterIds = incidentPerimeters
            .filter(ip => ip.incidentId === incidentId)
            .map(ip => ip.perimeterId);

          const incAffectedPerimeters: any[] = [];

          allAffectedPerimeters.forEach((affectedPerimeter: any) => {
            const parentPerimeter = response.allPerimeters.find(
              (p: any) => p.id === affectedPerimeter.perimeterId
            );
            if (parentPerimeter && incPerimeterIds.includes(parentPerimeter.id)) {
              // This perimeter is directly tagged to incident
              incAffectedPerimeters.push(affectedPerimeter);
              // Now collect descendants
              allAffectedPerimeters.forEach((descendant: any) => {
                if (
                  descendant.ancestors &&
                  descendant.ancestors.includes(parentPerimeter.id)
                ) {
                  incAffectedPerimeters.push(descendant);
                }
              });
            }
          });
          this._dictionaryAffectedPerimeters[incidentId] = incAffectedPerimeters;
        });
        return this._dictionaryAffectedPerimeters;
      }),
      catchError((error) => {
        console.error('Error in collectIncidentAffectedPerimetersAndBeaconsPlayback', error);
        return of({});
      })
    );
  }

  private collectPerimeterBeacons(perimeter: any, allBeacons: any[]): any[] {
    return allBeacons.filter(beacon => beacon.perimeterId === perimeter.id);
  }

  // Helper: Replace the id field in each object with the value from another field
  replaceHistoryId<T extends Record<string, any>>(array: T[], objectIdFieldName: string): T[] {
    return array.map(item => ({
      ...item,
      id: item[objectIdFieldName]
    }));
  }

  // Helper: Filter records by timeslice (start, end)
  filterByTimeslice<T extends { validAt: any }>(array: T[], start: any, end: any): T[] {
    return array.filter(item =>
      this.isChildTimesliceWithinParentTimeslice([start, end], item.validAt)
    );
  }

  fixInfinity(validAt: [string, string]): [string, string] {
    const fixed: [string, string] = [...validAt];
    if (fixed[0] === 'Infinity') {
      fixed[0] = '0000-00-00T00:00:00.000Z';
    }
    if (fixed[1] === 'Infinity') {
      fixed[1] = '9999-12-31T23:59:59.999Z';
    }
    return fixed;
  }

  // Checks if child timeslice overlaps with parent timeslice
  isChildTimesliceWithinParentTimeslice(
    parentValidAt: [string, string],
    childValidAt: [string, string]
  ): boolean {
    const parent = this.fixInfinity(parentValidAt);
    const child = this.fixInfinity(childValidAt);

    if (
      moment(child[1]).isBefore(moment(parent[0])) ||
      moment(parent[1]).isBefore(moment(child[0]))
    ) {
      return false;
    }
    return true;
  }
  private transforms = {
    trackingIncident: (incident: any, incidentOrgs: any[], device: any, user: any) => {
      // Implement your transformation logic here
      return {
        ...incident,
        incidentOrgs,
        device,
        user
      };
    }
  };

  updatePerimeterSafetyStatus(incidentId: string, timestamp: number): void {
    let stats;
    let closestTimestamp;
    if (this.sessionOptionsService.getMode && this.sessionOptionsService.getMode() === 'Playback Mode') {
      closestTimestamp = this.getClosestTimestamp(timestamp);
      if (closestTimestamp === this.lastClosestTimestamp) {
        return;
      }
      if (closestTimestamp) {
        stats = this.orgnizationUtilService.data.playbackBuildingViewData?.["" + closestTimestamp];
        this.lastClosestTimestamp = closestTimestamp;
      }
    } else {
      stats = this.deviceService.data.userStatusByIncident?.[incidentId];
    }

    if (stats) {
      if (_.isEqual(stats, this.orgnizationUtilService.data.userStatusByIncidentLast?.[incidentId])) {
        return;
      }
      this.resetPerimeterStatus(this.orgnizationUtilService.data.userStatusByIncidentLast?.[incidentId]);
      this.resetPerimetersForIncident(incidentId, closestTimestamp);
      const rooms = _.filter(stats, (o) => o.type === 'ROOM');
      _.forEach(rooms, (room) => {
        const perimeter = this.orgnizationUtilService.data.myPerimeters?.[room.id];
        if (perimeter) {
          if (room.danger && parseInt(room.danger) > 0) {
            perimeter.setSafetyStatusByStats('unsafe');
            this.threeJsBuildingIntegration.setSafetyStatusByStats(perimeter, 'unsafe');
          } else if (room.unknown && parseInt(room.unknown) > 0) {
            perimeter.setSafetyStatusByStats('unknown');
            this.threeJsBuildingIntegration.setSafetyStatusByStats(perimeter, 'unknown');
          } else if (room.safe && parseInt(room.safe) > 0) {
            perimeter.setSafetyStatusByStats('safe');
            this.threeJsBuildingIntegration.setSafetyStatusByStats(perimeter, 'safe');
          }
          if (typeof perimeter.setMaterialFromStatus === 'function') {
            perimeter.setMaterialFromStatus();     
            this.threeJsBuildingIntegration.setMaterialFromStatus(perimeter);
          }
        }
      });
      if (!this.orgnizationUtilService.data.userStatusByIncidentLast) this.orgnizationUtilService.data.userStatusByIncidentLast = {};
      this.orgnizationUtilService.data.userStatusByIncidentLast[incidentId] = stats;
    }
  }

  getClosestTimestamp(timestamp: number): number | undefined {
    const keys = _.keys(this.orgnizationUtilService.data.playbackBuildingViewDataDict || {});
    const diff = _.map(keys, (o) => timestamp - parseInt(o));
    const diff1 = _.filter(diff, (o) => !(o < 0));
    const min = _.min(diff1);
    let timestampToUse;
    if (!_.isUndefined(min)) {
      timestampToUse = Math.round(timestamp - min);
    }
    return timestampToUse;
  }

  resetPerimetersForIncident(incidentId: string, timestamp?: number): void {
    if (this.sessionOptionsService.getMode && this.sessionOptionsService.getMode() === 'Playback Mode') {
      const building = _.find(this.orgnizationUtilService.data.myBuildings, (b: any) => {
        let hasPlaybackIncident = false;
        if (b.incidents && b.incidents.length > 0) {
          _.forEach(b.incidents, (incident: any) => {
            if (incident.eventId === incidentId) {
              hasPlaybackIncident = true;
            }
          });
        }
        return hasPlaybackIncident;
      });
      if (!building) {
        return;
      }
      const statsDict = this.orgnizationUtilService.data.playbackBuildingViewDataDict?.["" + timestamp] || {};
      _.forEach(building.floorPerimeters, (floor: any) => {
        _.forEach(floor.perimeters, (perimeter: any) => {
          if (!(perimeter.arcAngelId && statsDict[perimeter.arcAngelId])) {
            perimeter.resetSafetyStatusByStats(perimeter);
            this.threeJsBuildingIntegration.resetSafetyStatusByStats(perimeter);
          }
        });
      });
    } else {
      const statsDict = this.deviceService.data.userStatusByIncidentDict?.[incidentId] || {};
      if (this.orgnizationUtilService.data.myBuildings === undefined || this.orgnizationUtilService.data.myBuildings.length === 0) {
        return;
      }
      _.forEach(this.orgnizationUtilService.data.myBuildings, (building: any) => {
        if (
          building.incidents &&
          building.incidents.length > 0 &&
          building.incidents[0].eventId === incidentId
        ) {
          _.forEach(building.floorPerimeters, (floor: any) => {
            _.forEach(floor.perimeters, (perimeter: any) => {
              if (!(perimeter.arcAngelId && statsDict[perimeter.arcAngelId])) {
                perimeter.resetSafetyStatusByStats(perimeter);
                this.threeJsBuildingIntegration.resetSafetyStatusByStats(perimeter);
              }
            });
          });
        }
      });
    }
  }

  resetPerimeterStatus(stats: any): void {
    const rooms = _.filter(stats, (o) => o.type === 'ROOM');
    _.forEach(rooms, (room) => {
      const perimeter = this.orgnizationUtilService.data.myPerimeters?.[room.id];
      if (perimeter) {
        perimeter.resetSafetyStatusByStats(perimeter);
        this.threeJsBuildingIntegration.resetSafetyStatusByStats(perimeter);
      }
    });
  }

  // Single request: returns a Promise-like (RxJS Observable) for a single timestamp
  getUserStatusByPerimeterForPlaybackSingle(incidentId: string, timestamp: string) {
    const param = {
      timestamp: timestamp,
    };
    const url = ConfigurationManagerService.settings.BaseURL + `db/Incident/${incidentId}/countDevicesByPerimeterAndStatusHistory`;
    // 'db/incidentFindCountDevicesByPerimeterAndStatusHistory';
    return this.api.post<any>(url, param).pipe(
      catchError((err) => {
        // Optionally log error
        return of([]);
      })
    );
  }

  getCampusDeviceByPerimeterAndStatusHistory(incidentId: string, timestamp: string): Observable<any[]> {
    const param = {
      timestamp: timestamp,
    };
    const url = ConfigurationManagerService.settings.BaseURL + `db/Incident/${incidentId}/countCampusDeviceByPerimeterAndStatusHistory`;
    return this.api.post<any>(url, param).pipe(
      catchError((err) => {
        // Optionally log error
        return of([]);
      })
    );
  }

  // Multi request: returns a Promise-like (RxJS Observable) for a range of timestamps
  getUserStatusByPerimeterForPlaybackMulti(incidentId: string, startTimestamp: number) {
    if (this.isPullingPlaybackBuildingViewData) {
      return of(false);
    }

    // First determine the incident type
    const campuses = this.orgnizationUtilService.data?.myCampus?.campuses || [];
    const campusIds = new Set(campuses.map((c: any) => String(c.campusId)));

    return this.getIncident(incidentId).pipe(
      map((response: any) => {
        const incident = response?.data?.items?.[0];
        if (incident) {
          const incidentPerimeterIds = incident.affectedPerimeters?.map((p: any) => String(p.perimeterId)) || [];
          return incidentPerimeterIds.some((perimId: string) => campusIds.has(perimId));
        }
        return false; // Default to building incident
      }),
      switchMap((isCampusIncident: boolean) => {
        const calls: { [key: string]: any } = {};

        for (let i = 0; i < 1; i++) {
          const t = startTimestamp + i * 1000;
          const date = moment(t).toISOString();

          if (!this.orgnizationUtilService.data.playbackBuildingViewData?.[t + '']) {
            if (isCampusIncident) {
              // For campus incidents, onlu use campus API
              calls['' + t] = forkJoin({
                campus: this.getCampusDeviceByPerimeterAndStatusHistory(incidentId, date)
              });
            } else {
              // For building incidents, only use building API
              calls['' + t] = forkJoin({
                building: this.getUserStatusByPerimeterForPlaybackSingle(incidentId, date)
              });
            }
          }
        }

        if (_.keys(calls).length === 0) {
          return of(true);
        }

        const t1 = new Date().getTime();
        this.isPullingPlaybackBuildingViewData = true;

        return forkJoin(calls).pipe(
          map((results) => {
            const t2 = new Date().getTime();
            _.forEach(results, (v: any, k) => {
              // Handle both cases - with or without campus property
              const buildingResult = v.building || [];
              const campusResult = v.campus || [];

              const result = [...buildingResult, ...campusResult];

              this.orgnizationUtilService.data.playbackBuildingViewData[k] = result;

              const dict: any = {};

              _.forEach(result, (d: any) => {
                dict[d.id] = d;

                if (d.type === 'CAMPUS') {
                  dict['campus'] = d;   // store campus entry separately
                }
              });

              this.orgnizationUtilService.data.playbackBuildingViewDataDict[k] = dict;
            });
            this.isPullingPlaybackBuildingViewData = false;
            return true;
          }),
          catchError((error) => {
            // Optionally: console.error(error);
            this.isPullingPlaybackBuildingViewData = false;
            return of(false);
          })
        );
      }),
      catchError((error) => {
        console.error('Error determining incident type:', error);
        return of(false);
      })
    );
  }

  getPlaybackProcall(eventId: string): Observable<any[]> {
    const empty = { count: 0, rows: [] };

    const paramsIncident = {
      attributes: ["status", "name", "createdAt", "updatedAt"],
      where: {
        id: eventId,
        type: "SUPPORT",
      },
      limit: 1,
    };
    const paramsIncidentDevice = {
      attributes: ["deviceId"],
      where: {
        incidentId: eventId,
      },
      limit: 1,
    };

    return forkJoin({
      incident: this.api.post<any>(
        ConfigurationManagerService.settings.BaseURL + 'db/Incident/findAndCountAllFromOrganization',
        paramsIncident
      ),
      incidentDevice: this.api.post<any>(
        ConfigurationManagerService.settings.BaseURL + 'db/IncidentDeviceHistory/findAndCountAllFromOrganization',
        paramsIncidentDevice
      ),
    }).pipe(
      switchMap(data => {
        if (
          !data.incident?.rows?.length ||
          !data.incidentDevice?.rows?.length
        ) {
          return forkJoin({
            incident: of(data.incident),
            incidentDevice: of(data.incidentDevice),
            device: of(empty)
          });
        }

        const deviceId = data.incidentDevice.rows[0].deviceId;
        const paramsDevice = {
          attributes: ["userId"],
          where: { deviceId },
          limit: 1,
        };

        return forkJoin({
          incident: of(data.incident),
          incidentDevice: of(data.incidentDevice),
          device: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + 'db/DeviceHistory/findAndCountAllFromOrganization',
            paramsDevice
          ),
        });
      }),
      switchMap(data => {
        const deviceRows = data.device?.rows ?? [];
        if (!deviceRows.length) {
          return forkJoin({
            incident: of(data.incident),
            incidentDevice: of(data.incidentDevice),
            device: of(data.device),
            user: of(empty)
          });
        }

        const userId = deviceRows[0].userId;
        const incidentCreatedAt = data.incident?.rows?.[0]?.createdAt ?? data.incident?.rows?.[0]?.createdAt;

        return forkJoin({
          incident: of(data.incident),
          incidentDevice: of(data.incidentDevice),
          device: of(data.device),
          user: this.api.post<any>(
            ConfigurationManagerService.settings.BaseURL + 'db/UserHistory/findAndCountAllFromOrganization',
            { userId, at: incidentCreatedAt }
          ),
        });
      }),
      map(data => {
        const userRows = data.user?.rows ?? [];
        if (!userRows.length) {
          return [];
        }

        const incident = (data.incident?.rows ?? [])[0];
        const incidentDevice = (data.incidentDevice?.rows ?? [])[0];
        const device = (data.device?.rows ?? [])[0];
        const user = userRows[0];

        return [
          {
            id: incident.id,
            type: "procall",
            status: /^active$/i.test(incident.status)
              ? "open"
              : incident.status.toLowerCase(),
            parentId: undefined,
            title: incident.name,
            organizationId: "n.a.",
            createdAt: incident.createdAt,
            updatedAt: incident.updatedAt,
            startedAt: incident.createdAt,
            endedAt: incident.updatedAt,
            installationId: incidentDevice.deviceId,
            profileId: device.userId,
            profile: user,
          },
        ];
      }),
      catchError(error => {
        console.error(error);
        return of([]);
      })
    );
  }

  approveIncidentUpdate(eventId: string, updateData: any): Promise<any> {
    return this.establishIncidentOrgForLiveMode(eventId).toPromise().then(orgIdForIncident => {
      const url = `${ConfigurationManagerService.settings.BaseURL}db/IncidentUpdate/${updateData.id}/approve`;
      const body = { organizationId: orgIdForIncident };
      return this.api.put(url, body).toPromise();
    });
  }

  rejectIncidentUpdate(eventId: string, updateData: any): Promise<any> {
    return this.establishIncidentOrgForLiveMode(eventId).toPromise().then(orgIdForIncident => {
      const url = `${ConfigurationManagerService.settings.BaseURL}db/IncidentUpdate/${updateData.id}/reject`;
      const body = { organizationId: orgIdForIncident };
      return this.api.put(url, body).toPromise();
    });
  }

  publicizeIncidentUpdate(eventId: string, updateData: any): Promise<any> {
    return this.establishIncidentOrgForLiveMode(eventId).toPromise().then(orgIdForIncident => {
      const url = `${ConfigurationManagerService.settings.BaseURL}db/IncidentUpdate/${updateData.id}/publish`;
      const body = { organizationId: orgIdForIncident };
      return this.api.put(url, body).toPromise();
    });
  }

  privitizeIncidentUpdate(eventId: string, updateData: any): Promise<any> {
    return this.establishIncidentOrgForLiveMode(eventId).toPromise().then(orgIdForIncident => {
      const url = `${ConfigurationManagerService.settings.BaseURL}db/IncidentUpdate/${updateData.id}/unpublish`;
      const body = { organizationId: orgIdForIncident };
      return this.api.put(url, body).toPromise();
    });
  }

 
  establishIncidentOrgForLiveMode(incidentId: string): Observable<string> {
    if (this._managingOrgId) {
      return of(this._managingOrgId);
    }

    const params = {
      attributes: ['organizationId'],
      order: [['createdAt', 'ASC']],
      where: { incidentId }
    };

    // API call for orgs assigned to incident
    const incidentOrgs$ = this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + 'db/IncidentOrganization/findAndCountAll',
      params
    );

    // API call for perimeter incident orgs (implement this method as needed)
    const perimeterIncidentOrgs$ = this.getPerimeterIncidentOrgs(incidentId);

    return forkJoin({
      incidentOrgs: incidentOrgs$,
      perimeterIncidentOrgs: perimeterIncidentOrgs$
    }).pipe(
      switchMap(result => {
        const incidentOrgIds = (result.incidentOrgs.rows || []).map((org: any) => org.organizationId);
        const allPotentialOrgsForIncident = _.union(incidentOrgIds, result.perimeterIncidentOrgs);
        // This returns Observable<string>
        return this.establishOrgIdToUseForLiveMode(allPotentialOrgsForIncident);
      })
    );
  }

  getPerimeterIncidentOrgs(incidentId: string): Observable<string[]> {
    // First, get perimeters for incident
    const params = {
      attributes: ['incidentId', 'perimeterId'],
      where: { incidentId }
    };

    return this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + 'db/IncidentPerimeter/findAndCountAll',
      params
    ).pipe(
      switchMap(result => {
        const perimeterIds = (result.rows || [])
          .filter((ip: any) => ip.incidentId === incidentId)
          .map((ip: any) => ip.perimeterId);

        if (!perimeterIds.length) {
          return of([]);
        }

        const orgParams = {
          attributes: ['organizationId'],
          where: { perimeterId: perimeterIds }
        };

        return this.api.post<any>(
          ConfigurationManagerService.settings.BaseURL + 'db/OrganizationPerimeter/findAndCountAll',
          orgParams
        );
      }),
      map(response => {
        if (!response) return [];
        const orgIds = (response.rows || []).map((row: any) => row.organizationId);
        return this.getOrgDescendants(orgIds);
      }),
      catchError(error => {
        console.error('Error in getPerimeterIncidentOrgs', error);
        return of([]);
      })
    );
  }


  // private _managingOrgId: string | undefined;

  establishOrgIdToUseForLiveMode(descendantOrgIds: string[]): Observable<string> {
    if (this._managingOrgId) {
      return of(this._managingOrgId);
    }


    const params = {
      where: { code: 'papi.db.Incident.createPerimeter' }
    };

    // Get orgs user can manage incidents for
    return this.api.post<any>(
      ConfigurationManagerService.settings.BaseURL + 'db/ActionSessionWithOrganizationIds/findAndCountAll',
      params
    ).pipe(
      switchMap(response => {
        const orgIdsUserCanManageIncidents = response.rows?.[0]?.organizationIds || [];
        const orgsAvailForIncident = _.intersection(orgIdsUserCanManageIncidents, descendantOrgIds);

        if (orgsAvailForIncident.length > 1) {
          // Prompt user to choose org (implement runChooseOrgDialog as needed)
          return this.runChooseOrgDialog(orgsAvailForIncident);
        }
        // If just one, use it
        return of(orgsAvailForIncident[0]);
      }),
      map((managingOrgId: string) => {
        this._managingOrgId = managingOrgId;
        return this._managingOrgId;
      })
    );
  }

  runChooseOrgDialog(orgIds: string[], title?: string): Observable<string> {
    const orgs = orgIds.map(orgId => this.orgnizationUtilService.myOrgs[orgId]);
    if (orgIds.length === 1) {
      return of(orgIds[0]);
    }

    const params = {
      organizations: orgs,
      title: title ?? 'Choose Organization'
    };

    // WindowService should be injected in your constructor
    // and openModalWindow should return a Promise or Observable
    return from(
      this.windowService.openModalWindow(
        params,
        'ChooseOrgDialogController',
        'components/arcangel/chooseorg/choose-org-dialog.html'
      ).then((response: any) => response.arcAngelId)
    );
  }
  // // Example runChooseOrgDialog implementation (replace with your modal/dialog logic)
  // runChooseOrgDialog(orgIds: string[]): Observable<string> {
  //   // You should open a modal/dialog here and return an observable of the selected orgId
  //   // For now, just return the first one for demonstration
  //   return of(orgIds[0]);
  // }
  getOrgDescendants(orgIds: string[]): string[] {
    orgIds = orgIds || [];
    const myOrgs = this.orgnizationUtilService.myOrgs || {};
    return _.uniq(_.concat(
      orgIds,
      _.flatten(
        orgIds.map(orgId =>
          myOrgs[orgId] && myOrgs[orgId].descendants
            ? myOrgs[orgId].descendants
            : []
        )
      )
    ));
  }
  private _twilioOrgIds: string[] | null = null;

  getTwilioOrgs(): Observable<any[]> {
    if (this._twilioOrgIds) {
      return of(this._twilioOrgIds);
    }
    const params = {
      order: [["organizationId", "ASC"]],
      attributes: ["organizationId"],
      limit: 10000
    };
     const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.OrganizationPhone;
    return this.api.post<any>(url, params).pipe(
      map(response => {
        this._twilioOrgIds = _.map(response?.rows ?? [], "organizationId");
        return this._twilioOrgIds;
      }),
      catchError(error => {
        console.error('Error loading Twilio orgs:', error);
        return of([]);
      })
    );
  }
  clearIllumDataCache(): void {
    this._twilioOrgIds = null;
  }
   getTravelData(options: { start: string; end: string }): Observable<any[]> {
    // 1. Fetch Calendar Itineraries
    const itineraryParams = {
      where: {
        during: { $overlap: [options.start, options.end] }
      },
      limit: 10000
    };

    return this.api
      .post<any>(
        '/db/CalendarItinerary/findAndCountAllWithCalendarFromOrganization',
        itineraryParams
      )
      .pipe(
        switchMap((itineraryResults: any) => {
          const calendarItineraryData = itineraryResults?.rows || [];
          const calendarItineraryIds = _.orderBy(
            _.uniq(_.map(calendarItineraryData, 'id'))
          );

          if (!calendarItineraryIds.length) return of([]);

          // 2. Fetch Calendar Events for those itineraries
          const eventParams = {
            attributes: ['id', 'calendarId', 'place', 'position'],
            where: {
              calendarItineraryId: calendarItineraryIds
            },
            limit: 10000
          };

          return this.api
            .post<any>(
              '/db/CalendarEvent/findAndCountAllWithCalendarEventContextsFromOrganization',
              eventParams
            )
            .pipe(
              map((eventResults: any) => {
                const calendarEventData: Record<string, any> = {};
                (eventResults?.rows || []).forEach((row: any) => {
                  calendarEventData[row.id] = row;
                });

                // Build calendarId -> userId dictionary
                const calendarToUserDict: Record<string, any> = {};
                calendarItineraryData.forEach((it: any) => {
                  if (it.Calendar && it.Calendar.id && it.Calendar.userId) {
                    calendarToUserDict[it.Calendar.id] = it.Calendar.userId;
                  }
                });

                // Build trips array
                let trips: any[] = [];
                Object.values(calendarEventData).forEach((event: any) => {
                  if (
                    event.CalendarEventContexts &&
                    event.CalendarEventContexts.length > 0
                  ) {
                    const trip = { ...event.CalendarEventContexts[0].data };
                    trip.userId =
                      event.calendarId && calendarToUserDict[event.calendarId]
                        ? calendarToUserDict[event.calendarId]
                        : null;
                    trip.calendarEventId = event.id;
                    trips.push(trip);
                  }
                });

                // Build pnr -> eventId dictionary
                const pnrToEventDict: Record<string, string[]> = {};
                trips.forEach((trip: any) => {
                  if (trip.calendarEventId) {
                    if (pnrToEventDict[trip.pnr]) {
                      pnrToEventDict[trip.pnr].push(trip.calendarEventId);
                    } else {
                      pnrToEventDict[trip.pnr] = [trip.calendarEventId];
                    }
                  }
                });

                // Uniq by pnr
                trips = _.uniqBy(trips, 'pnr');

                // Attach calendarEvents array to each trip
                trips.forEach((trip: any) => {
                  const events = (pnrToEventDict[trip.pnr] || []).map((eventId) => {
                    const oldEvent = calendarEventData[eventId];
                    return {
                      place: oldEvent?.place,
                      position: oldEvent?.position
                    };
                  });
                  trip.calendarEvents = events;
                });

                return trips;
              })
            );
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      );
  }
  getAirportInfo(iataCodes: string[]): Observable<Record<string, any>> {
  // Remove falsy, dedupe, and sort codes
    const codes = Array.from(new Set((iataCodes || []).filter(Boolean))).sort();

    if (!codes.length) {
      return of({});
    }

    const params = {
      attributes: ['name', 'iataCode', 'position'],
      where: { iataCode: codes },
      limit: 10000
    };

    return this.api
      .post<any>(
        '/db/AirportLookup/findAndCountAll',
        params
      )
      .pipe(
        map((results: any) => {
          const airportInfo: Record<string, any> = {};
          (results?.rows || []).forEach((row: any) => {
            airportInfo[row.iataCode] = row;
          });
          return airportInfo;
        }),
        catchError((error) => {
          console.error('Error loading airport info:', error);
          return of({});
        })
      );
    }

  updateEventDetails(
    obj: any,
    name: any,
    description: any,
    status: any,
    medGeo: any,
    lowGeo: any
  ): Observable<any> {
    return this.establishIncidentOrgForLiveMode(obj.id).pipe(
      switchMap((orgIdForIncident: any) => {
        const calls: Observable<any>[] = [];
        if (medGeo) {
          calls.push(
            this.updateOrCreateIncidentGeofence(orgIdForIncident, obj.id, 'MEDIUM', medGeo).pipe(
              catchError(() => of(null))
            )
          );
        }
        if (lowGeo) {
          calls.push(
            this.updateOrCreateIncidentGeofence(orgIdForIncident, obj.id, 'LOW', lowGeo).pipe(
              catchError(() => of(null))
            )
          );
        }

        return (calls.length ? forkJoin(calls) : of([null])).pipe(map(() => orgIdForIncident));
      }),
      switchMap((orgIdForIncident: any) =>
        this.getIncident(obj.id).pipe(
          map((incidentResp: any) => ({ orgIdForIncident, incident: incidentResp?.data?.items?.[0] })),
          catchError((err) => {
            return of({ orgIdForIncident, incident: null });
          })
        )
      ),
      switchMap(({ orgIdForIncident, incident }: { orgIdForIncident: any; incident: any }) => {
        if (!incident) {
          return of(null);
        }

        const currentLabel = incident.label ?? incident.name ?? '';
        const currentDescription = incident.description ?? '';
        const labelUnchanged = currentLabel === name;
        const descUnchanged = currentDescription === description;

        if (labelUnchanged && descUnchanged) {
          return of(incident);
        }

        const body = {
          organizationId: orgIdForIncident,
          description,
          name
        };
        const updateUrl = `${ConfigurationManagerService.settings.BaseURL}db/Incident/${obj.id}/updateIncident`;
        return this.api.patch<any>(updateUrl, body).pipe(
          catchError((err) => {
            return of(null);
          })
        );
      }),
      catchError((err) => {
        return of(null);
      })
    );
  }

  updateOrCreateIncidentGeofence(
    orgIdForIncident: string,
    incidentId: string,
    rating: string,
    geometry: any[]
  ): Observable<any> {
    const baseUrl = ConfigurationManagerService.settings.BaseURL;

    const flipLocationsNoZ = (coords: any[] = []) =>
      (coords || []).map((c: any) => {
        if (!Array.isArray(c) || c.length < 2) return c;
        return [c[1], c[0]];
      });

    const findParams = {
      where: {
        incidentId: incidentId,
        rating: rating
      },
      attributes: ['id'],
      limit: 10000
    };

    return this.api.post<any>(
      baseUrl + ConfigurationManagerService.settings.GeofenceFindAndCountAllFromOrganization,
      findParams
    ).pipe(
      switchMap((resp: any) => {
        const rows = resp?.rows ?? [];
        const geofenceId = rows && rows.length > 0 ? rows[0].id : null;

        const polygonCoords = flipLocationsNoZ(geometry || []);
        const geometryPayload = {
          type: 'Polygon',
          coordinates: [polygonCoords]
        };

        const body = {
          organizationId: orgIdForIncident,
          incidentId: incidentId,
          rating: rating,
          geometry: geometryPayload
        };

        if (geofenceId) {
          const updateUrl = `${baseUrl}db/Geofence/${geofenceId}`;
          return this.api.put<any>(updateUrl, body).pipe(
            catchError(err => {
              console.error('Failed to update geofence', err);
              return of(null);
            })
          );
        } else {
          const createUrl = baseUrl + (ConfigurationManagerService.settings.CreateGeofence || 'db/Incident/createGeofence');
          return this.api.post<any>(createUrl, body).pipe(
            catchError(err => {
              console.error('Failed to create geofence', err);
              return of(null);
            })
          );
        }
      }),
      catchError(err => {
        console.error('Error finding/creating/updating geofence', err);
        return of(null);
      })
    );
  }
}